import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, Phone, Sparkles } from "lucide-react";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { StageBadge, PrioridadeBadge } from "@/components/common/Badges";
import { ScoreBadge } from "@/components/leads/ScoreBadge";
import { AdiarRapido } from "@/components/leads/AdiarRapido";
import { RegistrarTentativa } from "@/components/leads/RegistrarTentativa";
import { useLeadDrawer } from "@/lib/leadDrawer";
import { fetchFilaHoje, fetchMotivos } from "@/lib/leads";
import { fetchLeadScores, fetchLeadsParados } from "@/lib/insights";
import { fetchExpenseCategories } from "@/lib/financeiro";
import { seedExpenseCategoriesSeNecessario, seedMotivosSeNecessario } from "@/lib/seed";
import { formatDateTime, formatRelativeToNow } from "@/lib/format";
import type { AdiarLeadResultado, Lead, LeadComResponsavel, LeadScore, VLeadParado } from "@/lib/types";

const CORTES_PARADOS = [7, 14, 30] as const;

export default function FilaHojePage() {
  const { openLead } = useLeadDrawer();
  const queryClient = useQueryClient();
  const [mostrarParados, setMostrarParados] = useState(false);
  const [corteParados, setCorteParados] = useState<(typeof CORTES_PARADOS)[number]>(7);

  const motivosQuery = useQuery({ queryKey: ["motivos"], queryFn: () => fetchMotivos() });
  const categoriasQuery = useQuery({ queryKey: ["expenseCategories"], queryFn: fetchExpenseCategories });
  const seedMutation = useMutation({
    mutationFn: async () => {
      await Promise.all([seedMotivosSeNecessario(), seedExpenseCategoriesSeNecessario()]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["motivos"] });
      queryClient.invalidateQueries({ queryKey: ["expenseCategories"] });
    },
  });
  const precisaSeed =
    (motivosQuery.data && motivosQuery.data.length === 0) ||
    (categoriasQuery.data && categoriasQuery.data.length === 0);

  const filaQuery = useQuery({ queryKey: ["filaHoje"], queryFn: fetchFilaHoje });
  const scoresQuery = useQuery({ queryKey: ["leadScores"], queryFn: fetchLeadScores });
  const paradosQuery = useQuery({
    queryKey: ["leadsParados", corteParados],
    queryFn: () => fetchLeadsParados(corteParados),
    enabled: mostrarParados,
  });

  const scoreMap = useMemo(() => {
    const m = new Map<string, LeadScore>();
    for (const s of scoresQuery.data ?? []) m.set(s.lead_id, s);
    return m;
  }, [scoresQuery.data]);

  function invalidarTudo() {
    queryClient.invalidateQueries({ queryKey: ["filaHoje"] });
    queryClient.invalidateQueries({ queryKey: ["leadsParados"] });
  }

  function onLeadAtualizado(lead: Lead) {
    // Auto-save ja aconteceu no servidor; so precisamos que a fila reflita
    // o novo proximo_contato_em (o lead pode ter saido da fila de hoje).
    invalidarTudo();
    queryClient.invalidateQueries({ queryKey: ["lead", lead.id] });
  }

  function onAdiado(_resultado: AdiarLeadResultado) {
    invalidarTudo();
  }

  return (
    <div>
      <h1>Fila de hoje</h1>

      {precisaSeed && (
        <div className="banner-danger" role="alert" style={{ marginBottom: 16 }}>
          <Sparkles className="icon" size={18} aria-hidden="true" />
          <span>
            Configuração inicial pendente: taxonomia de motivos/categorias ainda não existe neste projeto
            Firebase.
          </span>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={seedMutation.isPending}
            onClick={() => seedMutation.mutate()}
            style={{ marginLeft: 12 }}
          >
            {seedMutation.isPending ? "Configurando…" : "Configurar agora"}
          </button>
        </div>
      )}

      {filaQuery.isLoading && <LoadingState label="Carregando fila de hoje…" />}
      {filaQuery.isError && (
        <ErrorState message={(filaQuery.error as Error).message} onRetry={() => filaQuery.refetch()} />
      )}

      {filaQuery.data && (
        <>
          <Grupo
            titulo="Atrasados"
            tone="danger"
            leads={filaQuery.data.atrasados}
            scoreMap={scoreMap}
            onOpen={openLead}
            onLeadAtualizado={onLeadAtualizado}
            onAdiado={onAdiado}
          />
          <Grupo
            titulo="Hoje"
            tone="primary"
            leads={filaQuery.data.hoje}
            scoreMap={scoreMap}
            onOpen={openLead}
            onLeadAtualizado={onLeadAtualizado}
            onAdiado={onAdiado}
          />

          {filaQuery.data.atrasados.length === 0 && filaQuery.data.hoje.length === 0 && (
            <EmptyState
              celebratory
              title="Fila vazia"
              description="Nenhum lead com contato vencido ou marcado para hoje. Bom trabalho!"
            />
          )}
        </>
      )}

      <section className="fila-grupo">
        <button
          type="button"
          className="pill-toggle"
          aria-pressed={mostrarParados}
          aria-expanded={mostrarParados}
          onClick={() => setMostrarParados((v) => !v)}
        >
          <History className="icon" size={14} aria-hidden="true" />
          {mostrarParados ? "Ocultar" : "Mostrar"} leads parados
        </button>

        {mostrarParados && (
          <div style={{ marginTop: 12 }}>
            <p className="field-hint">
              Sem movimentação (nem tentativa, nem mudança de etapa) há um tempo. Leads adiados para o futuro não
              entram aqui — foi uma decisão consciente de esperar, não esquecimento.
            </p>
            <div className="chip-group" role="radiogroup" aria-label="Corte de dias parado" style={{ marginBottom: 12 }}>
              {CORTES_PARADOS.map((dias) => (
                <button
                  key={dias}
                  type="button"
                  role="radio"
                  aria-checked={corteParados === dias}
                  className="chip"
                  onClick={() => setCorteParados(dias)}
                >
                  <span className="chip-text">{dias}+ dias</span>
                </button>
              ))}
            </div>

            {paradosQuery.isLoading && <LoadingState label="Carregando leads parados…" />}
            {paradosQuery.isError && (
              <ErrorState
                message={(paradosQuery.error as Error).message}
                onRetry={() => paradosQuery.refetch()}
              />
            )}
            {paradosQuery.data?.length === 0 && (
              <EmptyState title="Nenhum lead parado" description={`Nada com ${corteParados}+ dias sem movimentação.`} />
            )}
            {paradosQuery.data?.map((lead) => (
              <ParadoItem
                key={lead.lead_id}
                lead={lead}
                score={scoreMap.get(lead.lead_id)}
                onOpen={() => openLead(lead.lead_id)}
                onAdiado={onAdiado}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Grupo({
  titulo,
  tone,
  leads,
  scoreMap,
  onOpen,
  onLeadAtualizado,
  onAdiado,
}: {
  titulo: string;
  tone: "danger" | "primary";
  leads: LeadComResponsavel[];
  scoreMap: Map<string, LeadScore>;
  onOpen: (id: string) => void;
  onLeadAtualizado: (lead: Lead) => void;
  onAdiado: (resultado: AdiarLeadResultado) => void;
}) {
  if (leads.length === 0) return null;
  return (
    <section className="fila-grupo">
      <div className="fila-grupo__titulo">
        <h2 style={{ margin: 0 }}>{titulo}</h2>
        <span className={`badge badge--${tone}`}>{leads.length}</span>
      </div>
      {leads.map((lead) => (
        <FilaItem
          key={lead.id}
          lead={lead}
          score={scoreMap.get(lead.id)}
          onOpen={() => onOpen(lead.id)}
          onLeadAtualizado={onLeadAtualizado}
          onAdiado={onAdiado}
        />
      ))}
    </section>
  );
}

function FilaItem({
  lead,
  score,
  onOpen,
  onLeadAtualizado,
  onAdiado,
}: {
  lead: LeadComResponsavel;
  score: LeadScore | undefined;
  onOpen: () => void;
  onLeadAtualizado: (lead: Lead) => void;
  onAdiado: (resultado: AdiarLeadResultado) => void;
}) {
  return (
    <article className="fila-item">
      <div className="fila-item__row">
        <div className="fila-item__main">
          <button type="button" className="table-row-button fila-item__empresa" onClick={onOpen}>
            {lead.empresa}
          </button>
          <div className="fila-item__meta">
            <StageBadge etapa={lead.etapa} />
            <PrioridadeBadge prioridade={lead.prioridade} />
            <ScoreBadge score={score} />
            <span>{lead.cidade ?? "Cidade não informada"}</span>
            {lead.telefone && (
              <span className="icon-text">
                <Phone className="icon" size={14} aria-hidden="true" />
                {lead.telefone}
              </span>
            )}
            <span>{lead.responsavel?.nome ?? "Sem responsável"}</span>
            <span title={formatDateTime(lead.proximo_contato_em)}>
              Contato: {formatRelativeToNow(lead.proximo_contato_em)}
            </span>
          </div>
        </div>
        <div className="fila-item__actions">
          <button type="button" className="btn btn--secondary btn--sm" onClick={onOpen}>
            Abrir ficha
          </button>
        </div>
      </div>
      <div className="fila-item__expand">
        <AdiarRapido leadId={lead.id} empresa={lead.empresa} onAdiado={onAdiado} />
        <div style={{ marginTop: 12 }}>
          <RegistrarTentativa lead={lead} onLeadAtualizado={onLeadAtualizado} />
        </div>
      </div>
    </article>
  );
}

function ParadoItem({
  lead,
  score,
  onOpen,
  onAdiado,
}: {
  lead: VLeadParado;
  score: LeadScore | undefined;
  onOpen: () => void;
  onAdiado: (resultado: AdiarLeadResultado) => void;
}) {
  return (
    <article className="fila-item">
      <div className="fila-item__row">
        <div className="fila-item__main">
          <button type="button" className="table-row-button fila-item__empresa" onClick={onOpen}>
            {lead.empresa}
          </button>
          <div className="fila-item__meta">
            <StageBadge etapa={lead.etapa} />
            <PrioridadeBadge prioridade={lead.prioridade} />
            <ScoreBadge score={score} />
            <span>{lead.cidade ?? "Cidade não informada"}</span>
            <span className="badge badge--neutral">{lead.dias_parado}d parado</span>
            <span className="field-hint">{lead.dias_na_etapa}d nesta etapa</span>
          </div>
        </div>
        <div className="fila-item__actions">
          <button type="button" className="btn btn--secondary btn--sm" onClick={onOpen}>
            Abrir ficha
          </button>
        </div>
      </div>
      <div className="fila-item__expand">
        <AdiarRapido leadId={lead.lead_id} empresa={lead.empresa} onAdiado={onAdiado} />
      </div>
    </article>
  );
}
